<script setup>
// Панель стрімера поверх стрічки чату — і на /live, і в доку OBS (/chat-dock).
//
// Стрімер веде ефір, дивлячись у чат, тож усе потрібне посеред трансляції
// тут: пресети одним кліком (нагорі — ними перемикають ефір найчастіше),
// скільки глядачів і як біжить чат, назва й доступ. Повна панель (графіки,
// історія ефірів, редагування пресетів, оверлей, гайд OBS) — док /stream-dock;
// ключі трансляції — лише в адмінці Strapi → «Панель стрімера».
//
// Ключів трансляції тут немає свідомо: док чату часто сам потрапляє в кадр.

const emit = defineEmits(['close'])

const { state, error, busy, uptime, updateSettings, applyPreset, saveCurrentAsPreset } =
  useStreamStudio()

const media = useStrapiMedia

const fmt = (value) => new Intl.NumberFormat('uk-UA').format(Math.round(value || 0))
const dec = (value) =>
  new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 1 }).format(value || 0)

const clock = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0))
  const pad = (n) => String(n).padStart(2, '0')
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`
}

// Кольори серій — ті самі, що на графіках в адмінці: колір іде за
// сутністю (сайт / Twitch), а не за місцем на графіку
const SITE = '#0891c9'
const TWITCH = '#8b5cf6'

// --- Назва ---
//
// Поле йде за сервером, поки його не чіпали: назву могли змінити в адмінці
const title = ref('')
const titleDirty = ref(false)

watch(
  () => state.value?.settings?.title,
  (value) => {
    if (!titleDirty.value) title.value = value || ''
  },
  { immediate: true }
)

const saveTitle = async () => {
  if (await updateSettings({ title: title.value })) titleDirty.value = false
}

const set = (patch) => updateSettings(patch)

// --- Пресети ---
const presetName = ref('')
const savingPreset = ref(false)

const savePreset = async () => {
  const name = presetName.value.trim()
  if (!name) return
  if (await saveCurrentAsPreset(name)) {
    presetName.value = ''
    savingPreset.value = false
  }
}

// --- Міні-графік: глядачі за останні пів години ---
const chart = computed(() => {
  const samples = state.value?.samples || []
  if (samples.length < 2) return null

  const W = 300
  const H = 64
  const max = Math.max(4, ...samples.map((s) => Math.max(s.site || 0, s.twitch || 0)))
  const step = W / (samples.length - 1)
  const line = (key) =>
    samples.map((s, i) => `${(i * step).toFixed(1)},${(H - ((s[key] || 0) / max) * (H - 4) - 2).toFixed(1)}`).join(' ')

  return { W, H, max, site: line('site'), twitch: line('twitch') }
})

const onKey = (e) => {
  if (e.key === 'Escape') emit('close')
}

onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <div class="z-30 absolute inset-0 flex flex-col bg-base-300">
    <div
      class="flex flex-shrink-0 items-center gap-2 bg-base-200 px-2 py-1.5 border-base-content/10 border-b"
    >
      <span class="font-semibold text-xs">Панель стрімера</span>
      <span
        v-if="state"
        class="inline-flex items-center gap-1 px-1.5 rounded-full font-bold text-[10px]"
        :class="state.live ? 'bg-error/15 text-error' : 'bg-base-content/10 text-base-content/50'"
      >
        <span
          class="inline-block rounded-full w-1.5 h-1.5"
          :class="state.live ? 'bg-error animate-pulse' : 'bg-base-content/30'"
        />
        {{ state.live ? `LIVE ${clock(uptime)}` : 'офлайн' }}
      </span>
      <button
        class="ml-auto text-base-content/50 hover:text-base-content"
        title="Закрити (Esc)"
        @click="emit('close')"
      >
        ✕
      </button>
    </div>

    <p
      v-if="error"
      class="flex-shrink-0 bg-error/15 px-2 py-1 text-[11px] text-error text-center"
    >
      {{ error }}
    </p>

    <div
      v-if="!state"
      class="flex justify-center items-center text-base-content/50 text-xs grow"
    >
      {{ error ? 'Панель недоступна' : 'Завантажуємо…' }}
    </div>

    <div
      v-else
      class="flex flex-col gap-3 p-2.5 overflow-y-auto grow"
    >
      <!-- Пресети -->
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center">
          <span class="font-semibold text-[11px] text-base-content/60">Пресети</span>
          <button
            class="ml-auto text-[11px] text-primary hover:underline"
            @click="savingPreset = !savingPreset"
          >
            {{ savingPreset ? 'Скасувати' : 'Зберегти поточні' }}
          </button>
        </div>

        <form
          v-if="savingPreset"
          class="flex gap-1"
          @submit.prevent="savePreset"
        >
          <input
            v-model="presetName"
            type="text"
            maxlength="80"
            class="input-bordered min-w-0 input input-sm grow"
            placeholder="Назва пресета"
          />
          <button
            class="btn btn-sm btn-primary"
            :disabled="busy || !presetName.trim()"
          >
            Зберегти
          </button>
        </form>

        <p
          v-if="!state.presets.length"
          class="text-[11px] text-base-content/50"
        >
          Пресетів ще немає — налаштуйте ефір і натисніть «Зберегти поточні».
        </p>

        <div
          v-for="preset in state.presets"
          :key="preset.id"
          class="flex items-center gap-2 p-1.5 border rounded-lg"
          :class="
            state.activePresetId === preset.id
              ? 'border-primary/50 bg-primary/10'
              : 'border-base-content/10 bg-base-200'
          "
        >
          <div
            class="flex flex-shrink-0 justify-center items-center bg-base-300 bg-cover bg-center rounded w-7 aspect-[2/3] text-[10px] text-base-content/30"
            :style="preset.poster ? { backgroundImage: `url(${media(preset.poster.thumb)})` } : null"
          >
            <span v-if="!preset.poster">—</span>
          </div>
          <div class="min-w-0 grow">
            <div class="font-semibold text-xs truncate">{{ preset.name }}</div>
            <div class="text-[10px] text-base-content/50 truncate">
              {{ preset.title || 'без назви' }}
            </div>
          </div>
          <button
            class="btn btn-xs"
            :class="state.activePresetId === preset.id ? 'btn-ghost' : 'btn-primary'"
            :disabled="busy"
            @click="applyPreset(preset.id)"
          >
            {{ state.activePresetId === preset.id ? 'Ще раз' : 'Увімкнути' }}
          </button>
        </div>
      </div>

      <!-- Просто зараз -->
      <div class="gap-1.5 grid grid-cols-2">
        <div class="col-span-2 bg-primary/10 px-2.5 py-2 border border-primary/25 rounded-lg">
          <div class="font-semibold text-[10px] text-base-content/60 uppercase tracking-wide">
            Глядачів зараз
          </div>
          <div class="flex items-end gap-2">
            <span class="font-extrabold text-2xl text-base-content tabular-nums leading-tight">{{
              fmt(state.viewers.total)
            }}</span>
            <span class="mb-0.5 text-[11px] text-base-content/60">
              <i
                class="inline-block mr-0.5 rounded-full w-1.5 h-1.5"
                :style="{ background: SITE }"
              />сайт {{ fmt(state.viewers.site) }} ·
              <i
                class="inline-block mr-0.5 rounded-full w-1.5 h-1.5"
                :style="{ background: TWITCH }"
              />Twitch {{ fmt(state.viewers.twitch) }}
            </span>
          </div>

          <svg
            v-if="chart"
            :viewBox="`0 0 ${chart.W} ${chart.H}`"
            preserveAspectRatio="none"
            class="block mt-1 w-full h-12"
            role="img"
            aria-label="Глядачі за останні пів години"
          >
            <polyline
              :points="chart.twitch"
              fill="none"
              :stroke="TWITCH"
              stroke-width="2"
              vector-effect="non-scaling-stroke"
            />
            <polyline
              :points="chart.site"
              fill="none"
              :stroke="SITE"
              stroke-width="2"
              vector-effect="non-scaling-stroke"
            />
          </svg>
        </div>

        <div
          v-for="tile in [
            { label: 'У чаті сайту', value: fmt(state.viewers.chat) },
            { label: 'Повідомлень / с', value: dec(state.chat.perSecond), hint: `${fmt(state.chat.perMinute)} за хв` },
            { label: 'За ефір', value: fmt(state.chat.total), hint: `сайт ${fmt(state.chat.siteMessages)} · tw ${fmt(state.chat.twitchMessages)}` },
            { label: 'Писали', value: fmt(state.chat.siteChatters + state.chat.twitchChatters) },
            { label: 'Пік глядачів', value: fmt(state.session?.peakTotal || 0), hint: state.session ? `сер. ${dec(state.session.avgTotal)}` : '' },
            { label: 'Унікальних', value: fmt(state.session?.uniqueViewers || 0), hint: 'на сайті' }
          ]"
          :key="tile.label"
          class="bg-base-200 px-2.5 py-1.5 border border-base-content/10 rounded-lg min-w-0"
        >
          <div class="font-semibold text-[10px] text-base-content/60 truncate uppercase tracking-wide">
            {{ tile.label }}
          </div>
          <div class="font-bold text-base text-base-content tabular-nums">{{ tile.value }}</div>
          <div
            v-if="tile.hint"
            class="text-[10px] text-base-content/50 truncate"
          >
            {{ tile.hint }}
          </div>
        </div>
      </div>

      <!-- Назва -->
      <label class="flex flex-col gap-1">
        <span class="text-[11px] text-base-content/60">Назва трансляції</span>
        <div class="flex gap-1">
          <input
            v-model="title"
            type="text"
            maxlength="200"
            class="input-bordered min-w-0 input input-sm grow"
            placeholder="Олсіор дивиться…"
            @input="titleDirty = true"
            @keydown.enter.prevent="saveTitle"
          />
          <button
            class="btn btn-sm btn-primary"
            :disabled="busy || !titleDirty"
            @click="saveTitle"
          >
            OK
          </button>
        </div>
      </label>

      <!-- Доступ і чат -->
      <div class="gap-2 grid grid-cols-2">
        <label class="flex flex-col gap-1 min-w-0">
          <span class="text-[11px] text-base-content/60">Доступ</span>
          <select
            class="select-bordered w-full select select-sm"
            :value="state.settings.accessMode"
            :disabled="busy"
            @change="set({ accessMode: $event.target.value })"
          >
            <option
              v-for="mode in state.options.accessModes"
              :key="mode"
            >
              {{ mode }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 min-w-0">
          <span class="text-[11px] text-base-content/60">Чат біля плеєра</span>
          <select
            class="select-bordered w-full select select-sm"
            :value="state.settings.chatSource"
            :disabled="busy"
            @change="set({ chatSource: $event.target.value })"
          >
            <option
              v-for="source in state.options.chatSources"
              :key="source"
            >
              {{ source }}
            </option>
          </select>
        </label>
      </div>

      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            class="toggle toggle-primary toggle-sm"
            :checked="!state.settings.streamOffline"
            :disabled="busy"
            @change="set({ streamOffline: !$event.target.checked })"
          />
          Ефір на сайті
          <span class="text-[10px] text-base-content/50">{{
            state.settings.streamOffline ? '— /live показує «офлайн»' : ''
          }}</span>
        </label>
        <label class="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            class="toggle toggle-primary toggle-sm"
            :checked="state.settings.showInCatalog"
            :disabled="busy"
            @change="set({ showInCatalog: $event.target.checked })"
          />
          Картка в каталозі
        </label>
      </div>

      <!-- Повний док: графіки, історія, пресети, оверлей, гайд OBS.
           Новою вкладкою — у доці OBS це відкриє браузер, а не підмінить чат -->
      <a
        href="/stream-dock"
        target="_blank"
        rel="noopener"
        class="btn btn-sm btn-neutral"
      >
        Повна панель стрімера ↗
      </a>
      <p class="text-[10px] text-base-content/40">
        Графіки, історія ефірів, редагування пресетів, оверлей і гайд OBS — у повній панелі (її ж можна додати в
        OBS доком: /stream-dock). Ключі трансляції — лише в адмінці.
      </p>
    </div>
  </div>
</template>
